import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { fetchBotRead } from '@/services/botCalls';

/** A line of the Revenue Imports view's history (BAR-3031), as the bot returns it. */
export interface ImportHistoryLine {
  id: string;
  source: string;
  fileName: string;
  /** A manual entry's proof, served by the `finance-proof` bot. */
  proofBinaryId: string | null;
  imported: number;
  skipped: number;
  /** `Practitioner/<id>`. */
  actingUser: string | null;
  createdAt: string;
  noPatient: number;
}

export function useImportHistory(): {
  imports: ImportHistoryLine[];
  loading: boolean;
  error: string | undefined;
  reload: () => void;
} {
  const medplum = useMedplum();
  const [imports, setImports] = useState<ImportHistoryLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchBotRead<{ history: { imports: ImportHistoryLine[] } }>(medplum, 'create-treatment-payment', {
      operation: 'importHistory',
    })
      .then(({ history }) => {
        if (!cancelled) {
          setImports(history.imports);
          setError(undefined);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          // History from before the failure would read as current (BAR-3125).
          setImports([]);
          setError(err instanceof Error ? err.message : 'Could not load the import history');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, reloadKey]);

  return { imports, loading, error, reload };
}
